// src/templates/UniversalTemplate.js
import React, { useEffect } from 'react';
import Countdown from './shared/Countdown';
import {
  FloralCorner, GoldDivider, OrnateCorner, RosetteDivider,
  Stars, AngelWings, Dove, CrossIcon, WeddingRings, Laurel,
} from './shared/Decorations';
import {
  IconCake,
  IconBaby,
  IconGraduationCap,
  IconChurch,
  IconGift,
  IconShirt,
  IconCamera,
  IconSparkle,
  IconParty,
} from '../components/ui/Icons';

import Gallery from './shared/Gallery';
import AudioPlayer from '../components/AudioPlayer';

function useGoogleFonts(url, key) {
  useEffect(() => {
    if (!url) return;
    const id = `gf-font-${key}`;
    if (document.getElementById(id)) return;
    const el = document.createElement('link');
    el.id = id;
    el.rel = 'stylesheet';
    el.href = url;
    document.head.appendChild(el);
  }, [url, key]);
}

// ---- Decoración de fondo según style ----
function BackgroundDecor({ style, palette }) {
  const c = palette.primary;
  const soft = palette.secondary || palette.soft;

  if (style === 'floral') {
    return (
      <>
        <FloralCorner color={c} className="absolute top-0 left-0 w-40 h-40 opacity-50 pointer-events-none" />
        <FloralCorner color={c} className="absolute top-0 right-0 w-40 h-40 opacity-50 pointer-events-none -scale-x-100" />
        <FloralCorner color={c} className="absolute bottom-0 left-0 w-40 h-40 opacity-50 pointer-events-none -scale-y-100" />
        <FloralCorner color={c} className="absolute bottom-0 right-0 w-40 h-40 opacity-50 pointer-events-none -scale-100" />
      </>
    );
  }
  if (style === 'artdeco') {
    return (
      <>
        <div className="absolute inset-6 border pointer-events-none" style={{ borderColor: c, opacity: 0.25 }} />
        <div className="absolute inset-10 border pointer-events-none" style={{ borderColor: c, opacity: 0.12 }} />
      </>
    );
  }
  if (style === 'boho') {
    return (
      <>
        <div className="absolute top-0 left-0 w-32 h-32 rounded-full opacity-20 blur-3xl" style={{ background: c }} />
        <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full opacity-15 blur-3xl" style={{ background: soft }} />
      </>
    );
  }
  if (style === 'confetti' || style === 'comic') {
    return (
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="absolute w-2 h-6 rounded-sm opacity-60"
            style={{
              left: `${(i * 8.5) % 100}%`,
              top: `${(i * 13) % 90}%`,
              background: [c, palette.secondary, palette.tertiary].filter(Boolean)[i % 3],
              transform: `rotate(${i * 30}deg)`,
            }} />
        ))}
      </div>
    );
  }
  if (style === 'cloud' || style === 'star' || style === 'celestial') {
    return (
      <>
        <div className="absolute top-10 left-0 right-0 opacity-60 pointer-events-none">
          <svg viewBox="0 0 300 60" className="w-48 ml-8"><ellipse cx="80" cy="40" rx="50" ry="20" fill="white"/></svg>
        </div>
        <Stars color={c} className="absolute bottom-32 left-4 w-40 opacity-40" />
      </>
    );
  }
  if (style === 'flower') {
    return (
      <>
        <div className="absolute top-0 left-0 w-48 h-48 rounded-full opacity-15 blur-3xl" style={{ background: c }} />
        <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{ background: soft }} />
      </>
    );
  }
  if (style === 'minimal') {
    return (
      <>
        <div className="absolute top-8 left-8 w-12 h-px" style={{ background: c }} />
        <div className="absolute top-8 right-8 w-12 h-px" style={{ background: c }} />
      </>
    );
  }
  if (style === 'garden') {
    return (
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute top-0 left-0 w-32 h-32 opacity-30" viewBox="0 0 100 100">
          <circle cx="20" cy="20" r="4" fill={c} />
          <circle cx="40" cy="15" r="3" fill={c} />
          <circle cx="15" cy="45" r="3" fill={c} />
          <path d="M20 20 Q30 30 40 15 M20 20 Q15 35 15 45" stroke={c} fill="none" strokeWidth="0.5" />
        </svg>
      </div>
    );
  }
  if (style === 'urban' || style === 'elegant') {
    return (
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />
    );
  }
  if (style === 'gradient') {
    return (
      <>
        <div className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: c }} />
        <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: palette.secondary }} />
      </>
    );
  }
  return null;
}

// ---- Símbolo central según categoría ----
function CategorySymbol({ category, palette }) {
  const c = palette.primary;
  const cls = 'w-12 h-12 mx-auto';

  if (category === 'Matrimonios')
    return <WeddingRings color={c} className="w-20 h-12 mx-auto" />;
  if (category === 'Bautizos')
    return <AngelWings color={c} className="w-40 h-16 mx-auto" />;
  if (category === 'Cumpleaños')
    return <span style={{ color: c }}><IconCake className={cls} /></span>;
  if (category === 'Baby Shower')
    return <span style={{ color: c }}><IconBaby className={cls} /></span>;
  if (category === 'Graduaciones')
    return <span style={{ color: c }}><IconGraduationCap className={cls} /></span>;
  return <span style={{ color: c }}><IconSparkle className={cls} /></span>;
}

// ---- Etiquetas por categoría ----
const LABELS = {
  Matrimonios: {
    hero: 'Nuestra Boda',
    verse: 'Una frase para compartir',
    countdown: 'Faltan para nuestro gran día',
    ceremonyHeader: 'Ceremonias',
    civil: 'Civil',
    church: 'Religiosa',
    reception: 'Recepción',
    story: 'Nuestra Historia',
    message: 'Un mensaje para ti',
    closing: 'Tu presencia es el regalo más valioso.',
  },
  'Cumpleaños': {
    hero: '¡Estás invitado!',
    verse: 'Un mensaje especial',
    countdown: 'Faltan para la fiesta',
    ceremonyHeader: 'La Fiesta',
    reception: 'Celebración',
    story: 'Un poco de mí',
    message: 'Un mensaje para ti',
    closing: '¡Ven a celebrar conmigo!',
  },
  'Baby Shower': {
    hero: 'Baby Shower',
    verse: 'Con mucho amor',
    countdown: 'Faltan para conocer al bebé',
    ceremonyHeader: 'La Celebración',
    reception: 'Celebración',
    story: 'Nuestra espera',
    message: 'Un mensaje para ti',
    closing: 'Te esperamos con mucho amor.',
  },
  Bautizos: {
    hero: 'Sacramento del Bautismo',
    verse: 'Palabra de Dios',
    countdown: 'Faltan para este gran día',
    ceremonyHeader: 'Ceremonia',
    church: 'Iglesia',
    reception: 'Celebración',
    story: 'Nuestra historia',
    message: 'Un mensaje para ti',
    closing: 'Tu presencia es el mejor regalo.',
  },
  Graduaciones: {
    hero: 'Graduación',
    verse: 'Una frase para recordar',
    countdown: 'Cuenta regresiva',
    ceremonyHeader: 'La Ceremonia',
    reception: 'La Celebración',
    story: 'Mi camino',
    message: 'Un mensaje para ti',
    closing: 'Celebremos juntos este logro.',
  },
};

// ---- Nombre principal (por categoría) ----
function getHeroNames(template, data) {
  const d = data || {};
  if (template.category === 'Matrimonios') {
    return { line1: d.bride || 'María', line2: d.groom || 'Carlos', isPair: true };
  }
  if (template.category === 'Cumpleaños') {
    return { line1: d.name || 'Festejado', line2: d.age ? `¡Cumple ${d.age}!` : null };
  }
  if (template.category === 'Baby Shower') {
    return { line1: d.babyName || 'Bebé', line2: d.parents };
  }
  if (template.category === 'Bautizos') {
    return { line1: d.child || 'Niño/a', line2: null };
  }
  if (template.category === 'Graduaciones') {
    return { line1: d.graduate || 'Graduado', line2: d.career };
  }
  return { line1: data?.title || 'Evento', line2: null };
}

// ===============================================
// COMPONENTE PRINCIPAL
// ===============================================

//export default function UniversalTemplate({ template, data, title, theme, media = [] }) {
export default function UniversalTemplate({ template, data, title, theme, media = [], audio = null }) {
  useGoogleFonts(theme?.googleFonts, template?.slug);

  const p = theme?.palette || {};
  const f = theme?.fonts || {};
  const style = theme?.style || 'floral';
  const cat = template?.category || 'Matrimonios';
  const L = LABELS[cat] || LABELS.Matrimonios;

  const d = data || {};
  const date = d.date ? new Date(d.date) : null;
  const dateStr = date
    ? date.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const dateShort = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  const hero = getHeroNames(template, d);

  const hasCivil = !!(d.civilPlace || d.civilAddress);
  const hasChurch = !!(d.church || d.churchAddress);
  const hasReception = !!(d.place || d.address || d.reception);

  const cardStyle = {
    background: p.card,
    border: `1px solid ${p.cardBorder || p.primary + '30'}`,
    boxShadow: `0 20px 60px -25px ${p.primary}30`,
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden" style={{ background: p.bg, color: p.dark }}>
      <BackgroundDecor style={style} palette={p} />

      {/* ============ HERO ============ */}
      <section className="relative px-6 pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div className="max-w-3xl mx-auto text-center relative">
          <p className="text-[10px] sm:text-xs tracking-[0.5em] uppercase mb-6"
            style={{ color: p.primary, fontFamily: f.heading, fontWeight: 600 }}>
            {L.hero}
          </p>

          <CategorySymbol category={cat} palette={p} />

          <h1 className="mt-8 text-5xl sm:text-7xl leading-tight"
            style={{ fontFamily: f.heading, color: p.dark, fontWeight: 700 }}>
            {hero.line1}
          </h1>

          {hero.isPair && (
            <>
              <div className="my-4 flex items-center justify-center gap-4">
                <div className="h-px w-16" style={{ background: p.primary, opacity: 0.5 }} />
                <span className="text-3xl italic" style={{ fontFamily: f.script, color: p.primary }}>&amp;</span>
                <div className="h-px w-16" style={{ background: p.primary, opacity: 0.5 }} />
              </div>
              <h1 className="text-5xl sm:text-7xl leading-tight"
                style={{ fontFamily: f.heading, color: p.dark, fontWeight: 700 }}>
                {hero.line2}
              </h1>
            </>
          )}

          {hero.line2 && !hero.isPair && (
            <p className="mt-4 text-xl sm:text-2xl"
              style={{ fontFamily: f.body, color: p.primary }}>
              {hero.line2}
            </p>
          )}

          {dateShort && (
            <div className="mt-10">
              <div className="inline-flex flex-col items-center">
                <div className="h-px w-24 mb-4" style={{ background: p.primary, opacity: 0.4 }} />
                <p className="text-xs tracking-[0.4em] uppercase mb-2"
                  style={{ color: p.primary, fontFamily: f.heading }}>
                  Nos vemos el
                </p>
                <p className="text-2xl sm:text-3xl tracking-[0.1em] uppercase"
                  style={{ fontFamily: f.heading, color: p.dark, fontWeight: 500 }}>
                  {dateShort}
                </p>
              </div>
            </div>
          )}

          {title && (
            <p className="mt-8 text-lg italic max-w-xl mx-auto"
              style={{ fontFamily: f.script, color: p.muted }}>
              {title}
            </p>
          )}
        </div>
      </section>

      {/* ============ VERSÍCULO ============ */}
      {(d.verse || ['boda-clasica','boda-elegante','bautizo-dorado','bautizo-celestial','bautizo-jardin'].includes(template?.slug)) && (
        <section className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <div className="relative rounded-2xl px-8 sm:px-16 py-12 text-center" style={cardStyle}>
              <div className="text-6xl leading-none mb-2" style={{ color: p.primary, fontFamily: f.heading, opacity: 0.3 }}>"</div>
              <p className="text-lg sm:text-2xl leading-relaxed italic"
                style={{ fontFamily: f.script, color: p.dark }}>
                {d.verse || 'El amor todo lo cree, todo lo espera, todo lo soporta.'}
              </p>
              <div className="my-6"><GoldDivider color={p.primary} className="w-40 h-6 mx-auto opacity-70" /></div>
              <p className="text-xs tracking-[0.3em] uppercase"
                style={{ color: p.primary, fontFamily: f.heading }}>
                {d.verseRef || 'Referencia'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ============ CUENTA REGRESIVA ============ */}
      {date && (
        <section className="px-6 py-16 text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-[10px] tracking-[0.5em] uppercase mb-3"
              style={{ color: p.primary, fontFamily: f.heading }}>
              {L.countdown}
            </p>
            <p className="text-2xl sm:text-3xl mb-10 italic"
              style={{ fontFamily: f.script, color: p.dark }}>
              {dateStr}
            </p>
            <Countdown target={date} accent={p.primary} labelColor={p.muted} />
          </div>
        </section>
      )}

      {/* ============ HISTORIA ============ */}
      {d.story && (
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[10px] tracking-[0.5em] uppercase mb-3"
              style={{ color: p.primary, fontFamily: f.heading }}>
              {L.story}
            </p>
            <div className="my-6"><GoldDivider color={p.primary} className="w-40 h-6 mx-auto opacity-60" /></div>
            <p className="text-lg leading-loose italic whitespace-pre-line"
              style={{ fontFamily: f.script, color: p.dark, opacity: 0.85 }}>
              {d.story}
            </p>
          </div>
        </section>
      )}

      {/* ============ GALERÍA ============ */}
      <Gallery
        media={media}
        category={cat}
        accent={p.primary}
        text={p.dark}
        muted={p.muted}
        title="Nuestros Momentos"
        subtitle="Recuerdos"
        headingFont={f.heading}
      />

      {/* ============ CEREMONIAS / EVENTOS ============ */}
      {(hasCivil || hasChurch || hasReception) && (
        <section className="px-6 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[10px] tracking-[0.5em] uppercase mb-3"
                style={{ color: p.primary, fontFamily: f.heading }}>
                {L.ceremonyHeader}
              </p>
              <div className="my-4"><GoldDivider color={p.primary} className="w-40 h-6 mx-auto opacity-60" /></div>
            </div>

            <div className={`grid gap-6 ${[hasCivil, hasChurch, hasReception].filter(Boolean).length === 3 ? 'md:grid-cols-3' : [hasCivil, hasChurch, hasReception].filter(Boolean).length === 2 ? 'md:grid-cols-2' : ''}`}>
              {hasCivil && (
                <div className="text-center rounded-2xl p-8" style={cardStyle}>
                  <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  <IconChurch className="w-full h-full" />
</div>
                  <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                    style={{ color: p.primary, fontFamily: f.heading }}>{L.civil}</p>
                  {d.civilPlace && <p className="text-xl mb-1" style={{ fontFamily: f.heading, color: p.dark, fontWeight: 500 }}>{d.civilPlace}</p>}
                  {d.civilAddress && <p className="text-sm" style={{ fontFamily: f.body, color: p.muted }}>{d.civilAddress}</p>}
                  {d.civilTime && <p className="mt-3 text-base italic" style={{ fontFamily: f.script, color: p.primary }}>{d.civilTime}</p>}
                </div>
              )}

              {hasChurch && (
                <div className="text-center rounded-2xl p-8" style={cardStyle}>
                  <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  <IconChurch className="w-full h-full" />
</div>
                  <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                    style={{ color: p.primary, fontFamily: f.heading }}>{L.church || 'Iglesia'}</p>
                  {d.church && <p className="text-xl mb-1" style={{ fontFamily: f.heading, color: p.dark, fontWeight: 500 }}>{d.church}</p>}
                  {d.churchAddress && <p className="text-sm" style={{ fontFamily: f.body, color: p.muted }}>{d.churchAddress}</p>}
                  {(d.churchTime || d.time) && <p className="mt-3 text-base italic" style={{ fontFamily: f.script, color: p.primary }}>{d.churchTime || d.time}</p>}
                </div>
              )}

              {hasReception && (
                <div className="text-center rounded-2xl p-8" style={cardStyle}>
                  <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  <IconParty className="w-full h-full" />
</div>
                  <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                    style={{ color: p.primary, fontFamily: f.heading }}>{L.reception}</p>
                  {(d.place || d.reception) && <p className="text-xl mb-1" style={{ fontFamily: f.heading, color: p.dark, fontWeight: 500 }}>{d.place || d.reception}</p>}
                  {d.address && <p className="text-sm" style={{ fontFamily: f.body, color: p.muted }}>{d.address}</p>}
                  {(d.receptionTime || d.time) && <p className="mt-3 text-base italic" style={{ fontFamily: f.script, color: p.primary }}>{d.receptionTime || d.time}</p>}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ DRESS CODE / REGALOS / HASHTAG ============ */}
      {(d.dressCode || d.giftRegistry || d.registryUrl || d.hashtag || d.giftIdea || d.theme) && (
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(d.theme || d.dressCode) && (
              <div className="text-center p-6 rounded-2xl" style={cardStyle}>
                <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  {d.theme ? <IconSparkle className="w-full h-full" /> : <IconShirt className="w-full h-full" />}
</div>
                <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                  style={{ color: p.primary, fontFamily: f.heading }}>
                  {d.theme ? 'Temática' : 'Dress Code'}
                </p>
                <p className="text-lg italic" style={{ fontFamily: f.script, color: p.dark }}>
                  {d.theme || d.dressCode}
                </p>
              </div>
            )}

            {(d.giftRegistry || d.registryUrl || d.giftIdea) && (
              <div className="text-center p-6 rounded-2xl" style={cardStyle}>
                <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  <IconGift className="w-full h-full" />
</div>
                <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                  style={{ color: p.primary, fontFamily: f.heading }}>Regalos</p>
                {(d.giftRegistry || d.registryUrl) ? (
                  <a href={d.giftRegistry || d.registryUrl} target="_blank" rel="noreferrer"
                    className="text-base underline decoration-dotted hover:opacity-70"
                    style={{ fontFamily: f.body, color: p.dark }}>
                    Ver mesa de regalos
                  </a>
                ) : (
                  <p className="text-base italic" style={{ fontFamily: f.script, color: p.dark }}>{d.giftIdea}</p>
                )}
              </div>
            )}

            {d.hashtag && (
              <div className="text-center p-6 rounded-2xl" style={cardStyle}>
                <div className="w-8 h-8 mx-auto mb-3" style={{ color: p.primary }}>
  <IconCamera className="w-full h-full" />
</div>
                <p className="text-[10px] tracking-[0.4em] uppercase mb-3"
                  style={{ color: p.primary, fontFamily: f.heading }}>Hashtag</p>
                <p className="text-lg" style={{ fontFamily: f.heading, color: p.dark, fontWeight: 500 }}>{d.hashtag}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============ MENSAJE ============ */}
      {d.description && (
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[10px] tracking-[0.5em] uppercase mb-4"
              style={{ color: p.primary, fontFamily: f.heading }}>{L.message}</p>
            <p className="text-lg sm:text-xl leading-loose italic"
              style={{ fontFamily: f.script, color: p.dark, opacity: 0.85 }}>
              "{d.description}"
            </p>
          </div>
        </section>
      )}

      {/* ============ NOTAS ============ */}
      {d.notes && (
        <section className="px-6 py-8">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-xs leading-relaxed" style={{ fontFamily: f.body, color: p.muted }}>{d.notes}</p>
          </div>
        </section>
      )}

      {/* ============ CIERRE ============ */}
      <section className="px-6 py-16 text-center">
        <CategorySymbol category={cat} palette={p} />
        <p className="mt-6 text-xl sm:text-2xl italic max-w-lg mx-auto leading-relaxed"
          style={{ fontFamily: f.script, color: p.dark }}>
          {L.closing}
        </p>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="pb-12 text-center">
        <p className="text-xs" style={{ fontFamily: f.body, color: p.muted, letterSpacing: '0.1em' }}>
          Invitación creada con <b style={{ color: p.primary, fontFamily: f.heading }}>Go Fiesta</b>
        </p>
      </footer>

      {audio && <AudioPlayer audio={audio} accent={p.primary} />}

    </div>
  );
}